import { useEffect, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import useLeadImport from '../../../../data/useLeadImport';
import LoadingWheel from '../../../shared/LoadingWheel';
import { CheckFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { hasVehicleIdentifier, leadImportCcid, readVinColumn } from '../../../../utils/leadImportIdentity';

function CSVToJSONConverter({ onResult }) {
  const { importLeads } = useLeadImport("CSVToJSONImporter");
  const isTME = CheckFeatureFlag("TME");
  const [isLoading, setIsLoading] = useState(false);
  const [importSource, setImportSource] = useState("fads");
  const [error, setError] = useState(false);

  const handleFileChange = (event) => {
    setIsLoading(true);
    setError(false);
    const file = event.target.files[0];

    if (!file) {
      setIsLoading(false);
      return;
    }

    // Shared processing: `results.data` is an array-of-arrays (header:false parse).
    const processResults = (results) => {
      var headers = getHeaders(results);
      var data = getData(results);

      var csvContent = Papa.unparse({
        fields: headers,
        data: data
      });

      const parser = parseType();
      parser(csvContent);
    };

    const name = file.name.toLowerCase();
    const isSpreadsheet =
      name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".xls");

    if (isSpreadsheet) {
      // Read the workbook in-browser and convert the first sheet to CSV so it
      // flows through exactly the same path as a real CSV upload.
      file
        .arrayBuffer()
        .then((buffer) => {
          const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
          // Prefer the B2B template's named sheet; otherwise fall back to the first.
          const sheetName = workbook.SheetNames.includes("ELV RFQ")
            ? "ELV RFQ"
            : workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const csv = XLSX.utils.sheet_to_csv(sheet);
          Papa.parse(csv, { header: false, complete: processResults });
        })
        .catch((err) => {
          console.log(err);
          setError(true);
          setIsLoading(false);
        });
      return;
    }

    Papa.parse(file, {
      header: false,
      complete: processResults,
    });
  };

  // Return correct header row per source
  const getHeaders = (results) => ({
    fads: getFadsHeaders(results),
    motorwise: results.data[0],
    removemycar: results.data[0],
    toyotaC2B: results.data[0],
    toyotaB2B: results.data[0],
  })[importSource];

  const getFadsHeaders = (results) => {
    const headerRow = results.data.find(
      row => row && row[0] && row[0].toString().startsWith("CollectID")
    );

    return headerRow ?? results.data[2]; // fallback if nothing matches
  }

  // Return correct data slice per source
  const getData = (results) => ({
    fads: results.data.slice(3),
    motorwise: results.data.slice(1),
    removemycar: results.data.slice(1),
    toyotaC2B: results.data.slice(1),
    toyotaB2B: results.data.slice(1),
  })[importSource];

  // Pick parser function
  const parseType = () => ({
    fads: fadsParser,
    motorwise: motorwiseParser,
    removemycar: removeMyCarParser,
    toyotaC2B: toyotaC2BParser,
    toyotaB2B: toyotaB2BParser,
  })[importSource];

  // Parse a UK date (DD/MM/YYYY) or an Excel serial number to an ISO string.
  const toIsoDate = (value) => {
    if (value === null || value === undefined) return null;
    const str = value.toString().trim();
    if (str === "") return null;

    // Excel serial date (e.g. saved as CSV from the B2B .xlsm)
    if (/^\d+(\.\d+)?$/.test(str)) {
      const serial = parseFloat(str);
      const ms = Math.round((serial - 25569) * 86400 * 1000); // 25569 = 1970-01-01 in Excel's 1900 date system
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d.toISOString();
    }

    // DD/MM/YYYY or DD-MM-YYYY
    const m = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (m) {
      let [, dd, mm, yyyy] = m;
      if (yyyy.length === 2) yyyy = "20" + yyyy;
      const d = new Date(Date.UTC(+yyyy, +mm - 1, +dd));
      return isNaN(d.getTime()) ? null : d.toISOString();
    }

    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  // Build an "Key: value | Key: value" notes string, skipping empty values.
  const buildNotes = (pairs) =>
    pairs
      .filter(([, v]) => v !== undefined && v !== null && v.toString().trim() !== "")
      .map(([k, v]) => `${k}: ${v.toString().trim()}`)
      .join(" | ");

  const saveNewLeads = (leads) => {
    if (!leads || leads.length === 0) {
      setIsLoading(false);
      setError(true);
      return;
    }

    var body = { leads };

    // console.log("Importing leads:", body);
    importLeads.mutate(body, {
      onSuccess: (res) => {
        // Surface the per-lead breakdown so the parent can show the results modal.
        const result = res?.data ?? { successful: [], unsuccessful: [] };
        onResult?.(result);
      },
      onError: (err) => {
        console.log(err);
        setError(true);
      },
      onSettled: () => setIsLoading(false),
    });
  };

  const fadsParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {
          var mapped = {
            CCID: lead["CCID"],
            Source: lead["Source"],
            Website: 2,
            Price: lead["Sales Price"],
            Commission: lead["Referral Fee"],
            PaymentType: lead["Payment Type"],
            AccountName: lead["Account Name"],
            AccountNumber: lead["Account Number"],
            SortCode: lead["Sort Code"],
            PaymentReference: lead["Payment Reference"],
            CardNumber: lead["Card Number"],
            PayeeDOB: lead["Payee DOB"],
            ChequeName: lead["Cheque Name"],
            ChequeNumber: lead["Cheque Number"],
            PremiumPriceUsed: lead["Premium Price Used"],
            Scheme: lead["Scheme"],
            VehicleGrade: lead["Vehicle Grade"],
            CentralPayment: lead["Central Payment"],
            Contact: {
              Fullname: `${lead["Customer Title"]} ${lead["Customer First Name"]} ${lead["Customer Last Name"]}`.trim(),
              Telephone1: lead["Customer Telephone (main)"],
              Telephone2: lead["Customer Telephone (alternative)"],
              Email: lead["Customer Email"],
              Organisation: lead["Customer Organisation"],
              Line1: `${lead["Customer Building"]} ${lead["Customer Street"]}`.trim(),
              Line2: lead["Customer Locality"],
              Town: lead["Customer Town"],
              County: lead["Customer County"],
              Postcode: lead["Customer Postcode"],
            },
            Vehicle: {
              Vrm: lead["VRM"],
              Vin: readVinColumn(lead),
              Make: lead["Make"],
              Model: lead["Model"],
              Telephone1: lead["Location Telephone (main)"],
              Telephone2: lead["Location Telephone (alternative)"],
              Line1: `${lead["Location Building"]} ${lead["Location Street"]}`.trim(),
              Line2: lead["Location Locality"],
              Town: lead["Location Town"],
              County: lead["Location County"],
              Postcode: lead["Location Postcode"],
              VehiclePremium: lead["Premium Price Used"] === "TRUE",
              IsDropOff: isTME,
            }
          };

          // A row with a plate or a VIN names a vehicle; VIN-only rows used to be dropped here.
          if (hasVehicleIdentifier(mapped.Vehicle)) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };

  const motorwiseParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {
          var mapped = {
            CCID: lead["id"],
            Source: lead["siteName"],
            Website: 4,
            Price: parseInt(lead["quotedValue"]) || 0,
            Commission: 30,
            Contact: {
              Fullname: `${lead["title"]} ${lead["firstName"]} ${lead["lastName"]}`.trim(),
              Telephone1: lead["phone"],
              Telephone2: lead["alternatePhone"],
              Email: lead["email"],
              Line1: lead["addressLine1"]?.trim(),
              Line2: lead["addressLine2"],
              Town: lead["addressLine3"],
              County: "",
              Postcode: lead["postcode"],
            },
            Vehicle: {
              Vrm: lead["vrm"],
              Vin: readVinColumn(lead),
              Make: lead["make"],
              Model: lead["model"],
              Telephone1: lead["phone"],
              Telephone2: lead["alternatePhone"],
              Line1: lead["addressLine1"]?.trim(),
              Line2: lead["addressLine2"],
              Town: lead["addressLine3"],
              County: "",
              Postcode: lead["postcode"],
              VehiclePremium: lead["premiumCalibration"] === "1",
              VehicleNotes: lead["userComments"],
              IsDropOff: isTME,
            }
          };

          // A row with a plate or a VIN names a vehicle; VIN-only rows used to be dropped here.
          if (hasVehicleIdentifier(mapped.Vehicle)) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };

  const removeMyCarParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {

          const status = lead["ApprovalStatusDescription"]?.trim().toLowerCase();
          if (status !== "arrange collection") {
            return; // skip this lead
          }

          // Strip HTML tags from notes
          const cleanNote = lead["Quote Notes"]
            ? lead["Quote Notes"].replace(/<[^>]*>/g, "").trim()
            : "";

          // Ignore "No Contact" notes
          const shouldIncludeNote =
            cleanNote &&
            cleanNote !== "A message was sent to the seller for No Contact";

          const mapped = {
            CCID: lead["QuoteId"],
            Source: "RemoveMyCar",
            Website: 8,
            Price: parseInt(lead["Price"]) || 0,
            Commission: 0,
            OfficeNotes: shouldIncludeNote ? cleanNote : "",
            Contact: {
              Fullname: lead["Customer Name"]?.trim(),
              Telephone1: lead["Phone"],
              Telephone2: "",
              Email: lead["Email"],
              Line1: "",
              Line2: lead["Locality"] || "",
              Town: lead["Town"],
              County: lead["County"],
              Postcode: lead["Postcode"],
            },
            Vehicle: {
              Vrm: lead["RegNo"],
              Vin: readVinColumn(lead),
              Make: lead["Combined_Make"],
              Model: lead["Combined_Model"],
              Telephone1: lead["Phone"],
              Telephone2: "",
              Line1: "",
              Line2: lead["Locality"] || "",
              Town: lead["Town"],
              County: lead["County"],
              Postcode: lead["Postcode"],
              VehiclePremium: false,
              IsDropOff: isTME,
            },
          };

          // A row with a plate or a VIN names a vehicle; VIN-only rows used to be dropped here.
          if (hasVehicleIdentifier(mapped.Vehicle)) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };

  const toyotaC2BParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {
          const scheduledCollectionOn = toIsoDate(lead["Collection Date"]);

          const mapped = {
            CCID: leadImportCcid({ Vrm: lead["Reg"], Vin: readVinColumn(lead) }),
            Source: lead["Site"]?.trim() || "Toyota - C2B",
            Website: 10, // LeadWebsite.ToyotaC2B
            Price: 0,
            Commission: 0,
            OfficeNotes: buildNotes([
              ["Site", lead["Site"]],
              ["Status", lead["job_status_group"]],
              ["Due Date", lead["Due Date"]],
              ["Year", lead["Year"]],
              ["Fuel", lead["Fuel"]],
              ["Drivetrain", lead["Drivetrain"]],
              ["Engine Code", lead["engine_code"]],
              ["Engine Boost", lead["Engine Boost"]],
            ]),
            Contact: {
              Fullname: "",
              Telephone1: "",
              Telephone2: "",
              Email: "",
              Line1: "",
              Line2: "",
              Town: "",
              County: "",
              Postcode: "",
            },
            Vehicle: {
              Vrm: lead["Reg"],
              Vin: readVinColumn(lead),
              Make: lead["Make"],
              Model: lead["Model"],
              VehiclePremium: false,
              IsDropOff: isTME,
              // Toyota ELV vehicles are end-of-life, so a COD is always required.
              CodRequired: true,
              ScheduledCollectionOn: scheduledCollectionOn,
              // A dated drop-off is confirmed stock, so mark it Purchased.
              ...(scheduledCollectionOn ? { FinancialDecision: 3 } : {}), // FinancialDecision.Purchased
            },
          };

          // A row with a plate or a VIN names a vehicle; VIN-only rows used to be dropped here.
          if (hasVehicleIdentifier(mapped.Vehicle)) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };

  const toyotaB2BParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {
          // Excel appends a "2" suffix to the duplicate header, so accept either.
          const insuranceCategory = lead["Insurance category"] ?? lead["Insurance category2"] ?? "";
          const scheduledCollectionOn = toIsoDate(lead["Delivery date"]);

          const mapped = {
            CCID: leadImportCcid({ Vrm: lead["Reg"], Vin: readVinColumn(lead) }),
            Source: lead["Supplier"]?.trim() || "Toyota - B2B",
            Website: 11, // LeadWebsite.ToyotaB2B
            Price: 0,
            Commission: 0,
            VehicleGrade: insuranceCategory,
            OfficeNotes: buildNotes([
              ["Supplier", lead["Supplier"]],
              ["Year", lead["Year"]],
              ["Fuel Type", lead["Fuel Type"]],
              ["Engine", lead["Engine"]],
              ["Transmission", lead["Transmission"]],
              ["Boost", lead["Boost"]],
              ["Keys", lead["Keys"]],
              ["Insurance Category", insuranceCategory],
              ["Purchase date", lead["Purchase date"]],
            ]),
            Contact: {
              Fullname: "",
              Telephone1: "",
              Telephone2: "",
              Email: "",
              Line1: "",
              Line2: "",
              Town: lead["Location"],
              County: "",
              Postcode: "",
            },
            Vehicle: {
              Vrm: lead["Reg"],
              Vin: readVinColumn(lead),
              Make: lead["Make"],
              Model: lead["Model"],
              Town: lead["Location"],
              VehiclePremium: false,
              IsDropOff: isTME,
              // Toyota ELV vehicles are end-of-life, so a COD is always required.
              CodRequired: true,
              ScheduledCollectionOn: scheduledCollectionOn,
              // A dated drop-off is confirmed stock, so mark it Purchased.
              ...(scheduledCollectionOn ? { FinancialDecision: 3 } : {}), // FinancialDecision.Purchased
            },
          };

          // A row with a plate or a VIN names a vehicle; VIN-only rows used to be dropped here.
          if (hasVehicleIdentifier(mapped.Vehicle)) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };


  if (isLoading) {
    return (
      <div className="flex justify-center">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="w-1/4 mb-12">
        <label className="w-full text-left">
          Import Source
        </label>
        <select className="w-full" value={importSource} onChange={(e) => setImportSource(e.target.value)}>
          <option value="fads">Fads</option>
          <option value="motorwise">Motorwise</option>
          <option value="removemycar">Remove My Car</option>
          {isTME && (
            <>
              <option value="toyotaC2B">Toyota - C2B</option>
              <option value="toyotaB2B">Toyota - B2B</option>
            </>
          )}
        </select>
      </div>

      <div className="mx-auto">
        <input
          id="import"
          type="file"
          accept=".csv,.txt,.xlsx,.xlsm,.xls"
          className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-8 py-4 text-xl font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          onChange={handleFileChange}
        />

        <div className="mt-4">
          {error && <p className="text-lg text-red-500">Error - Import Unsuccessful</p>}
        </div>
      </div>
    </div>
  );
}

export default CSVToJSONConverter;
